// Client-side MJML → HTML preview renderer. Used by SavedTemplates,
// AllTemplates, and EditCampaign's template picker.
//
// Why this exists: the prior implementation POSTed each template's MJML to
// the Node /mjml2html endpoint (one HTTP round-trip + Node compile each).
// For a 10-template list that's 2–5 seconds of network + server cost
// before any thumbnail can render. mjml-browser does the exact same compile
// in the user's browser in <50 ms with zero network. Switching to it makes
// list pages feel instant.
//
// We memoize by the MJML string itself so re-renders of the same component
// during search/filter/sort don't recompile. Cache is unbounded but capped
// in practice by the number of templates a user has — even thousands of
// distinct MJML strings is fine here (a few MB of cached HTML).
import mjml2html from 'mjml-browser';

const cache = new Map(); // key: mjml string -> { html, errors }

/**
 * Compile an MJML string to HTML. Synchronous. Memoized.
 * Returns the empty string on falsy input or compile failure (with an
 * `errors` array attached as a side property on the cached entry).
 */
export function renderMjml(mjml) {
	if (!mjml || typeof mjml !== 'string') return '';
	const cached = cache.get(mjml);
	if (cached) return cached.html;
	try {
		const result = mjml2html(mjml, {
			validationLevel: 'soft',
			minify: false,
			beautify: false,
		});
		const entry = {html: result.html || '', errors: result.errors || []};
		cache.set(mjml, entry);
		return entry.html;
	} catch (err) {
		// eslint-disable-next-line no-console
		console.warn('[mjmlPreview] compile failed:', err && err.message);
		cache.set(mjml, {html: '', errors: [err && err.message]});
		return '';
	}
}

/**
 * Pre-warm the cache for a list of templates by their MJML strings.
 * Returns a Promise that resolves once all are compiled. Compiles run
 * in microtasks via Promise.resolve().then so the UI stays responsive.
 */
export function prewarmPreviews(mjmlStrings) {
	return Promise.all(
		(mjmlStrings || []).map((m) =>
			Promise.resolve().then(() => renderMjml(m)),
		),
	);
}

export function clearPreviewCache() {
	cache.clear();
}
